import { intlTag, type Locale } from "./locales.ts";

/**
 * Numbers, percentages, money, dates, relative times, lists and plurals in the reader's language.
 * One implementation for app, mail, PDFs and voice (PLAN §6.10), built on Intl so Danish reads
 * "47 %", "1.234,5", "99 €" and "10. sep. 2026", and English "47%", "1,234.5", "€99" and "10 Sept 2026".
 */
export interface FormatOptions {
  /** IANA time zone for dates; the company's or the person's. Defaults to the runtime's zone. */
  timeZone?: string | undefined;
}

export type DateInput = Date | string | number;
export type DateStyle = "short" | "medium" | "long" | "full" | "monthYear" | "month" | "weekday" | "dayMonth";

export interface NumberFormatOptions {
  minDigits?: number;
  maxDigits?: number;
  /** "12k" / "12 t." style for tight places like axis labels and tiles. */
  compact?: boolean;
  signed?: boolean;
}

export interface Formatter {
  readonly locale: Locale;
  readonly tag: string;
  number(value: number, options?: NumberFormatOptions): string;
  integer(value: number): string;
  /** A ratio as a percentage: 0.47 → "47 %" (da) / "47%" (en). */
  percent(ratio: number, options?: { digits?: number; signed?: boolean }): string;
  /** A change as a signed percentage: 0.09 → "+9 %", -0.031 → "−3,1 %". */
  percentChange(ratio: number, options?: { digits?: number }): string;
  /** Money in its currency (ISO 4217): 99, "EUR" → "€99" (en) / "99 €" (da). */
  currency(amount: number, currency: string, options?: NumberFormatOptions): string;
  date(value: DateInput, style?: DateStyle): string;
  dateTime(value: DateInput): string;
  time(value: DateInput): string;
  /** "3 minutes ago", "yesterday", "in 2 weeks" / "for 3 minutter siden", "i går", "om 2 uger". */
  relative(value: DateInput, now?: DateInput): string;
  /** "a, b and c" / "a, b og c". */
  list(items: readonly string[], type?: "conjunction" | "disjunction" | "unit"): string;
  /** The plural category for a count, for choosing copy outside i18next. */
  plural(count: number, type?: "cardinal" | "ordinal"): Intl.LDMLPluralRule;
  /**
   * Text that carries numbers (an insight's headline, a note) set the way a native reader writes them: English
   * "65.8%" with no space, Danish "65,8 %" with a non-breaking one, and a number never wraps away from its unit.
   */
  tidy(text: string): string;
}

const NBSP = "\u00a0";
/** Units a number is never parted from, whatever the language. */
const UNITS = String.raw`(?:%|‰|kr\.?|DKK|EUR|USD|€|£|pts?|pct\.?|points?|dage?|days?|uger?|weeks?|timer|hours?|min\.?|sek\.?|stk\.?)`;
const NUMBER_UNIT = new RegExp(String.raw`(\d)[ \u00a0\u202f](?=${UNITS}(?![\p{L}\d]))`, "gu");

/** "65.8 %" → "65.8%" in English; "65,8 %" with a non-breaking space everywhere else. */
export function tidyText(text: string, locale: Locale): string {
  if (locale === "en") {
    return text.replace(/(\d)[ \u00a0\u202f]+%/g, "$1%").replace(NUMBER_UNIT, `$1${NBSP}`);
  }
  return text.replace(/(\d)[ \u00a0\u202f]*%/g, `$1${NBSP}%`).replace(NUMBER_UNIT, `$1${NBSP}`);
}

const cache = new Map<
  string,
  Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat | Intl.ListFormat | Intl.PluralRules
>();

function cached<
  T extends
    | Intl.NumberFormat
    | Intl.DateTimeFormat
    | Intl.RelativeTimeFormat
    | Intl.ListFormat
    | Intl.PluralRules,
>(key: string, make: () => T): T {
  let hit = cache.get(key) as T | undefined;
  if (!hit) {
    hit = make();
    cache.set(key, hit);
  }
  return hit;
}

function toDate(value: DateInput): Date {
  return value instanceof Date ? value : new Date(value);
}

const DATE_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: "numeric", month: "numeric", year: "numeric" },
  medium: { day: "numeric", month: "short", year: "numeric" },
  long: { day: "numeric", month: "long", year: "numeric" },
  full: { weekday: "long", day: "numeric", month: "long", year: "numeric" },
  monthYear: { month: "short", year: "numeric" },
  month: { month: "long" },
  weekday: { weekday: "long" },
  dayMonth: { day: "numeric", month: "short" },
};

const RELATIVE_STEPS: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number; limit: number }> = [
  { unit: "second", seconds: 1, limit: 45 },
  { unit: "minute", seconds: 60, limit: 45 * 60 },
  { unit: "hour", seconds: 3600, limit: 22 * 3600 },
  { unit: "day", seconds: 86400, limit: 6 * 86400 },
  { unit: "week", seconds: 7 * 86400, limit: 27 * 86400 },
  { unit: "month", seconds: 30 * 86400, limit: 320 * 86400 },
  { unit: "year", seconds: 365 * 86400, limit: Number.POSITIVE_INFINITY },
];

export function createFormat(locale: Locale, options: FormatOptions = {}): Formatter {
  const tag = intlTag(locale);
  const tz = options.timeZone;
  /** The 24-hour languages write the hour with two digits everywhere (»kl. 06.02«, never »kl. 6.02«); English keeps 6:02 AM. */
  const hour: "numeric" | "2-digit" = locale === "en" ? "numeric" : "2-digit";

  const numberFormat = (opts: Intl.NumberFormatOptions) =>
    cached(`n|${tag}|${JSON.stringify(opts)}`, () => new Intl.NumberFormat(tag, opts));
  const dateFormat = (opts: Intl.DateTimeFormatOptions) =>
    cached(
      `d|${tag}|${tz ?? ""}|${JSON.stringify(opts)}`,
      () => new Intl.DateTimeFormat(tag, { ...opts, timeZone: tz }),
    );

  const numberOptions = (o: NumberFormatOptions = {}): Intl.NumberFormatOptions => ({
    minimumFractionDigits: o.minDigits,
    maximumFractionDigits: o.maxDigits ?? (o.compact ? 1 : 2),
    notation: o.compact ? "compact" : "standard",
    signDisplay: o.signed ? "exceptZero" : "auto",
  });

  return {
    locale,
    tag,
    number: (value, o) => numberFormat(numberOptions(o)).format(value),
    integer: (value) => numberFormat({ maximumFractionDigits: 0 }).format(value),
    percent: (ratio, o = {}) =>
      tidyText(
        numberFormat({
          style: "percent",
          maximumFractionDigits: o.digits ?? (Math.abs(ratio) < 0.1 && ratio !== 0 ? 1 : 0),
          signDisplay: o.signed ? "exceptZero" : "auto",
        }).format(ratio),
        locale,
      ),
    percentChange: (ratio, o = {}) =>
      tidyText(
        numberFormat({
          style: "percent",
          maximumFractionDigits: o.digits ?? (Math.abs(ratio) < 0.1 && ratio !== 0 ? 1 : 0),
          signDisplay: "exceptZero",
        }).format(ratio),
        locale,
      ),
    currency: (amount, currency, o = {}) =>
      numberFormat({
        style: "currency",
        currency: currency.toUpperCase(),
        currencyDisplay: "narrowSymbol",
        // The minimum never exceeds the maximum, or Intl throws (a whole-number money cap on 12.5 did).
        minimumFractionDigits: Math.min(
          o.minDigits ?? (Number.isInteger(amount) || o.compact ? 0 : 2),
          o.maxDigits ?? 20,
        ),
        maximumFractionDigits: o.maxDigits ?? (Number.isInteger(amount) ? 0 : 2),
        notation: o.compact ? "compact" : "standard",
        signDisplay: o.signed ? "exceptZero" : "auto",
      }).format(amount),
    date: (value, style = "medium") => dateFormat(DATE_STYLES[style]).format(toDate(value)),
    dateTime: (value) =>
      dateFormat({
        day: "numeric",
        month: "short",
        year: "numeric",
        hour,
        minute: "2-digit",
      }).format(toDate(value)),
    time: (value) => dateFormat({ hour, minute: "2-digit" }).format(toDate(value)),
    relative: (value, now = new Date()) => {
      const diffSeconds = (toDate(value).getTime() - toDate(now).getTime()) / 1000;
      const abs = Math.abs(diffSeconds);
      const rtf = cached(`r|${tag}`, () => new Intl.RelativeTimeFormat(tag, { numeric: "auto" }));
      if (abs < 10) return rtf.format(0, "second");
      const step = RELATIVE_STEPS.find((s) => abs < s.limit) ?? RELATIVE_STEPS[RELATIVE_STEPS.length - 1];
      if (!step) return rtf.format(0, "second");
      return rtf.format(Math.round(diffSeconds / step.seconds), step.unit);
    },
    list: (items, type = "conjunction") =>
      cached(`l|${tag}|${type}`, () => new Intl.ListFormat(tag, { type, style: "long" })).format(items),
    plural: (count, type = "cardinal") =>
      cached(`p|${tag}|${type}`, () => new Intl.PluralRules(tag, { type })).select(count),
    tidy: (text) => tidyText(text, locale),
  };
}
